<script lang="ts">
	import { onMount } from 'svelte';

	interface Props {
		dataCy: string;
		src: string;
		alt: string;
		minScale?: number;
		maxScale?: number;
	}

	let { dataCy, src = '', alt = '', minScale = 0.5, maxScale = 3 }: Props = $props();

	let scale = $state(1);
	let lastDistance = $state(0);
	let translateX = $state(0);
	let translateY = $state(0);
	let lastCenterX = $state(0);
	let lastCenterY = $state(0);
	let isTwoFingerPanning = $state(false);
	let imgElement = $state<HTMLImageElement>();

	function getDistance(touches: TouchList): number {
		const dx: number = touches[0].clientX - touches[1].clientX;
		const dy: number = touches[0].clientY - touches[1].clientY;
		return Math.sqrt(dx * dx + dy * dy);
	}

	function getTouchCenter(touches: TouchList): { x: number; y: number } {
		return {
			x: (touches[0].clientX + touches[1].clientX) / 2,
			y: (touches[0].clientY + touches[1].clientY) / 2
		};
	}

	function resetZoomAndPan(): void {
		scale = 1;
		translateX = 0;
		translateY = 0;
	}

	$effect(() => {
		void src;
		resetZoomAndPan();
	});

	function handleTouchStart(e: TouchEvent): void {
		if (e.touches.length === 2) {
			e.preventDefault();
			lastDistance = getDistance(e.touches);
			const center = getTouchCenter(e.touches);
			lastCenterX = center.x;
			lastCenterY = center.y;
			isTwoFingerPanning = true;
		}
	}

	function handleTouchMove(e: TouchEvent): void {
		if (e.touches.length === 2) {
			e.preventDefault();
			const currentDistance: number = getDistance(e.touches);
			const scaleChange: number = currentDistance / lastDistance;

			scale *= scaleChange;
			scale = Math.min(Math.max(minScale, scale), maxScale);

			lastDistance = currentDistance;

			if (isTwoFingerPanning) {
				const currentCenter = getTouchCenter(e.touches);
				const deltaX = currentCenter.x - lastCenterX;
				const deltaY = currentCenter.y - lastCenterY;

				translateX += deltaX;
				translateY += deltaY;

				lastCenterX = currentCenter.x;
				lastCenterY = currentCenter.y;
			}
		}
	}

	function handleTouchEnd(e: TouchEvent): void {
		if (e.touches.length < 2) {
			lastDistance = 0;
			isTwoFingerPanning = false;
		}
	}

	onMount((): (() => void) => {
		if (imgElement) {
			const eventOptions: { passive: boolean } = { passive: false };

			imgElement.addEventListener('touchstart', handleTouchStart, eventOptions);
			imgElement.addEventListener('touchmove', handleTouchMove, eventOptions);
			imgElement.addEventListener('touchend', handleTouchEnd, eventOptions);
		}

		return (): void => {
			if (imgElement) {
				imgElement.removeEventListener('touchstart', handleTouchStart);
				imgElement.removeEventListener('touchmove', handleTouchMove);
				imgElement.removeEventListener('touchend', handleTouchEnd);
			}
		};
	});
</script>

<div class="overflow-hidden touch-none relative inline-block max-h-[80vh] w-full">
	<img
		data-cy={`${dataCy}-lightbox-image`}
		bind:this={imgElement}
		{src}
		{alt}
		class="transform-gpu origin-center max-w-full max-h-[80vh] select-none transition-transform duration-100 ease-out mx-auto"
		style="transform: scale({scale}) translate({translateX}px, {translateY}px);"
		draggable="false"
	/>
</div>
